import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, GlobalFilterOptions } from '@ot/shared/model';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchAll } from 'rxjs/operators';
import {
  Environment,
  EnvironmentConfigService,
  PlatformServiceName
} from '../environment';
import { Label } from '../models';
import {
  APIVehicle,
  ApplyVehicleLabelsRequest,
  ApplyVehicleLabelsResponse,
  ListVehiclesResponse,
  RemoveVehicleLabelsRequest,
  RemoveVehicleLabelsResponse,
  VehicleLabels,
  VehicleLabelsOperationRequest,
  VehicleLabelsOperationResponse
} from '../models/vehicle';

@Injectable({ providedIn: 'root' })
export class VehicleService {
  apiUrl: string;
  pageSize = '25000';

  constructor(
    @Inject(ENVIRONMENT) private environment: Environment,
    private environmentConfigService: EnvironmentConfigService,
    private http: HttpClient
  ) {
    this.apiUrl =
      this.environmentConfigService.getEnvironmentConfigs().apis.platform.url +
      '/' +
      this.environment.serviceUrlList[PlatformServiceName.VehicleService];
  }

  getVehiclesList(businessAccountId): Observable<ListVehiclesResponse> {
    return this.getVehiclesListPerPage(businessAccountId, '1', this.pageSize);
  }
  getVehiclesListPerPage(
    businessAccountId: string,
    page: string,
    pageSize: string
  ): Observable<ListVehiclesResponse> {
    const parameters: HttpParams = new HttpParams()
      .set('options.page_token', page)
      .set('options.page_size', pageSize);
    const options = { params: parameters };
    return this.http.get<ListVehiclesResponse>(
      `${this.apiUrl}/businessAccounts/${businessAccountId}/vehicles`,
      options
    );
  }

  getAllVehicles(businessAccountId): Observable<APIVehicle[]> {
    const observables: Observable<ListVehiclesResponse>[] = [];
    let vehicles: APIVehicle[] = [];
    return this.getVehiclesListPerPage(
      businessAccountId,
      '1',
      this.pageSize
    ).pipe(
      map((response: ListVehiclesResponse) => {
        for (let i = 1; i <= Number(response.totalPages); i++) {
          observables.push(
            this.getVehiclesListPerPage(
              businessAccountId,
              i.toString(),
              this.pageSize
            )
          );
        }
        if (observables.length !== 0) {
          return forkJoin(observables).pipe(
            map((listVehiclesResponses: ListVehiclesResponse[]) => {
              for (let j = 0; j < listVehiclesResponses.length; j++) {
                vehicles = vehicles.concat(listVehiclesResponses[j].vehicles);
              }
              return vehicles;
            })
          );
        } else {
          return of([]);
        }
      }),
      switchAll()
    );
  }

  applyVehicleLabels(
    businessAccountId: string,
    vehicleIds: string[],
    labels: Label[]
  ): Observable<VehicleLabels[]> {
    const url = `${this.apiUrl}/vehicles/labels:apply`;
    const request: ApplyVehicleLabelsRequest = this.buildPostLabelsRequest(
      businessAccountId,
      vehicleIds,
      labels
    );
    return this.http
      .post<ApplyVehicleLabelsResponse>(url, request)
      .pipe(map(this.mapPostLabelsResponse));
  }

  removeVehicleLabels(
    businessAccountId: string,
    vehicleIds: string[],
    labels: Label[]
  ): Observable<VehicleLabels[]> {
    const url = `${this.apiUrl}/vehicles/labels:remove`;
    const request: RemoveVehicleLabelsRequest = this.buildPostLabelsRequest(
      businessAccountId,
      vehicleIds,
      labels
    );
    return this.http
      .post<RemoveVehicleLabelsResponse>(url, request)
      .pipe(map(this.mapPostLabelsResponse));
  }

  private buildGeneralFilter(businessAccountId: string): GlobalFilterOptions {
    const value = businessAccountId;
    return {
      businessAccountIds: [{ value }]
    };
  }

  private buildPostLabelsRequest(
    businessAccountId: string,
    vehicleIds: string[],
    labels: Label[]
  ): VehicleLabelsOperationRequest {
    const globalFilter: GlobalFilterOptions = this.buildGeneralFilter(
      businessAccountId
    );
    return {
      generalFilter: globalFilter,
      vehicleFilter: {
        global: globalFilter,
        vehicleIds: vehicleIds.map(id => ({ value: id }))
      },
      labels
    };
  }

  private mapPostLabelsResponse(
    response: VehicleLabelsOperationResponse
  ): VehicleLabels[] {
    return response.updatedVehicles.map(vehicle => ({
      id: vehicle.id.value,
      customId: vehicle.customId,
      labels: vehicle.labels
    }));
  }
}
